import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Panel } from '@pitchero/react-ui';
import { openLoginPanel as openLoginPanelAction } from '../../store/auth/actions';
import { isLoggedIn as isLoggedInSelector } from '../../store/auth/selectors';
import {
  getPrimaryMemberId,
  isAssociated,
  isLoadingNetworkPanel as isLoadingNetworkPanelSelector,
  getLoggedInUser,
} from '../../store/membership/selectors';
import { PLACEHOLDER_AVATAR } from '../../lib/club-theme/avatar';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import AvailabilityBarLoadingState from './availability-bar-loading-state';
import AvailabilityStatusBar from './availability-status-bar';
import AvailabilityPanel from './panel';
import ApplyPlayerPanel from './apply-player-panel';
import JoinClubPanel from './join-club-panel';

const Availability = ({
  availabilities,
  club,
  date,
  isLoading,
  isLoadingNetworkPanel,
  isLoggedIn,
  memberId,
  onUpdateAvailability,
  openLoginPanel,
  teams,
  user,
  userIsAssociatedToClub,
}) => {
  const [activeAvailability, setActiveAvailability] = useState(null);
  const [joinPanelIsOpen, setJoinPanelIsOpen] = useState(false);
  const [applyPanelIsOpen, setApplyPanelOpen] = useState(false);

  const onSubmitHandler = (newStatus, newNote) => {
    onUpdateAvailability(activeAvailability.member.id, newStatus, newNote);
    setActiveAvailability(null);
  };

  if (isLoading || isLoadingNetworkPanel) {
    // Loading state
    return <AvailabilityBarLoadingState />;
  }

  if (!isLoggedIn) {
    // User not logged in
    return (
      <AvailabilityStatusBar
        isSelf
        member={{ avatar: PLACEHOLDER_AVATAR, firstName: '' }}
        onClick={() => openLoginPanel()}
      />
    );
  }

  if (!userIsAssociatedToClub && user) {
    // User is logged in but they are not associated to this club.
    const { avatar, firstName } = user;
    return (
      <>
        <AvailabilityStatusBar
          isSelf
          member={{ avatar, firstName }}
          onClick={() => setJoinPanelIsOpen(true)}
        />
        <JoinClubPanel isOpen={joinPanelIsOpen} onClose={() => setJoinPanelIsOpen(false)} />
      </>
    );
  }

  if (availabilities.length === 0 && user) {
    // User is logged in and associated to this club, but they are not being asked for availability
    // this is due to them not have appropriate player/parent role.
    const { avatar, firstName } = user;
    return (
      <>
        <AvailabilityStatusBar
          isSelf
          member={{ avatar, firstName }}
          onClick={() => setApplyPanelOpen(true)}
        />
        <ApplyPlayerPanel
          isOpen={applyPanelIsOpen}
          teamNames={teams.map((team) => team.name).join(',')}
          memberId={memberId}
          onClose={() => setApplyPanelOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      {availabilities.map((availability) => (
        <AvailabilityStatusBar
          key={availability.member.id}
          isSelf={availability.member.id === memberId}
          onClick={() => setActiveAvailability(availability)}
          {...availability}
        />
      ))}
      <Panel isOpen={!!activeAvailability} onClose={() => setActiveAvailability(null)}>
        {activeAvailability && (
          <AvailabilityPanel
            club={club}
            {...activeAvailability}
            date={date}
            onSubmit={onSubmitHandler}
            isActingOnSelf={memberId === get(activeAvailability, 'member.id')}
          />
        )}
      </Panel>
    </>
  );
};

Availability.defaultProps = {
  availabilities: [],
  memberId: null,
  user: null,
};

Availability.propTypes = {
  availabilities: PropTypes.arrayOf(
    PropTypes.shape({
      member: PropTypes.shape({
        avatar: PropTypes.avatar,
        name: PropTypes.string,
      }),
      memberId: PropTypes.number,
      status: PropTypes.oneOf(['available', 'not_set', 'not_sure', 'unavailable']),
    }),
  ),
  club: CLUB_PROP_TYPES.isRequired,
  date: PropTypes.string.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingNetworkPanel: PropTypes.bool.isRequired,
  memberId: PropTypes.number,
  user: PropTypes.shape(),
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ).isRequired,
  openLoginPanel: PropTypes.func.isRequired,
  onUpdateAvailability: PropTypes.func.isRequired,
  userIsAssociatedToClub: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  isLoggedIn: isLoggedInSelector(state),
  isLoadingNetworkPanel: isLoadingNetworkPanelSelector(state),
  memberId: getPrimaryMemberId(state, ownProps.clubId),
  user: getLoggedInUser(state),
  userIsAssociatedToClub: isAssociated(state, ownProps.clubId),
});

export default connect(mapStateToProps, { openLoginPanel: openLoginPanelAction })(Availability);
